import { RouteComponentProps, useHistory } from 'react-router-dom';
import { RegisterSummary } from '../../Registers/types/RegisterSummary';
import { useMutation, useQueryClient } from 'react-query';
import { useGetReturnUrl } from '../../../../Infrastructure/pageState/useGetReturnUrl';
import { makeRequest } from '@crx-dev/hss-react-infrastructure';
import { useFormik } from 'formik';
import * as yup from 'yup';
import React, { useCallback, useEffect } from 'react';
import { getFieldProps, Page, Form, Heading, Label, Inputs, GridLayout } from '@crx-dev/hss-react-components';
import { useUserInfo } from '../../../../Infrastructure/auth/UseUserInfo';
import { titleCase } from 'title-case';

interface QuarantineFormProps extends RouteComponentProps<{ registerId?: string }> {
    registerSummary: RegisterSummary;
}

function QuarantineForm({ match, registerSummary }: QuarantineFormProps) {
    const queryClient = useQueryClient();
    const returnUrl = useGetReturnUrl();
    const registerId = match.params.registerId;
    const history = useHistory();
    const userInfo = useUserInfo();

    const mutation = useMutation<unknown, unknown, any>(item =>
        makeRequest({
            method: 'POST',
            path: `/api/v1/controldrugregister/${registerId}/quarantine`,
            data: { ...item, user: userInfo.name }
        })
    );

    const formik = useFormik({
        initialValues: {
            quantity: 0,
            dateExpired: new Date(Date.now()),
            comments: '',
            addedBy: userInfo.name,
            balance: registerSummary.balance
        },
        validationSchema: yup.object().shape({
            quantity: yup
                .number()
                .moreThan(0)
                .required('Required')
                .max(
                    registerSummary.balance - registerSummary.quarantineBalance,
                    `The total expired quantity cannot exceed the total balance (${registerSummary.balance})`
                ),
            dateExpired: yup.date().required('Required'),
            addedBy: yup.string().required('Required')
        }),
        onSubmit: async values => {
            applyTitleCaseFormat();
            await mutation.mutateAsync(values);
            await queryClient.invalidateQueries(['register-summary', registerId]);
            history.push(returnUrl);
        }
    });

    useEffect(() => {
        formik.setFieldValue('balance', registerSummary.balance - Number(formik.values.quantity));
    }, [formik.values.quantity]);

    const handleCancelClick = useCallback(() => {
        history.push(returnUrl);
    }, []);

    const applyTitleCaseFormat = () => {
        formik.values.addedBy = titleCase(formik.values.addedBy);
    };

    return (
        <Page>
            <Page.Header
                title="Expired Stock Entry"
                subtitle={registerSummary.productName}
                onBackClick={() => history.push(returnUrl)}
            />
            <Page.Content>
                <Form onSubmit={formik.handleSubmit}>
                    <Form.Section>
                        <Heading>Entry Details</Heading>
                        <Form.Columns columnWidths="calc(50% - 12px) calc(50% - 12px)">
                            <>
                                <Label label="Date" htmlFor="dateExpired" />
                                <Inputs.DatePicker disabled {...getFieldProps(formik, 'dateExpired')} />
                            </>
                        </Form.Columns>
                    </Form.Section>
                    <Form.Section>
                        <Heading>Expiry Details</Heading>
                        <GridLayout marginBottom={25}>
                            <Form.Columns columnWidths="calc(50% - 12px) calc(50% - 12px)">
                                <>
                                    <Label label="Quantity Expired" htmlFor="quantity" />
                                    <Inputs.Number {...getFieldProps(formik, 'quantity')} decimal />
                                </>
                                <>
                                    <Label label="Added By" htmlFor="addedBy" />
                                    <Inputs.Text {...getFieldProps(formik, 'addedBy')} />
                                </>
                            </Form.Columns>
                        </GridLayout>
                        <>
                            <Label label="Comment" htmlFor="comment" />
                        </>
                        <Inputs.TextArea {...getFieldProps(formik, 'comment')} />
                    </Form.Section>
                    <Form.Section>
                        <Heading>Balance Details</Heading>
                        <Form.Columns columnWidths="calc(50% - 12px) calc(50% - 12px)">
                            <>
                                <Label label="Balance Preview" htmlFor="balance" />

                                <Inputs.Number disabled {...getFieldProps(formik, 'balance')} decimal />
                            </>
                        </Form.Columns>
                    </Form.Section>
                    <Form.Footer
                        onCancel={handleCancelClick}
                        isSubmitting={mutation.isLoading}
                        submitError={mutation.isError}
                        submitButtonLabel="CONFIRM ENTRY"
                    />
                </Form>
            </Page.Content>
        </Page>
    );
}

export default QuarantineForm;
